import { sanitizeHTML } from '@/lib/utils'
import Link from 'next/link'
import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'

interface WhyChooseUsData {
	anchor_title?: string
	heading?: {
		text?: string
		title?: string
	}
	text?: {
		text?: string
	}
	review_text?: {
		text?: string
	}
	buttons?: {
		menu_items_id: {
			href: string
			label: string
		}
	}[]
	image?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string
					text: string
				}
			}[]
		}
	}[]
	verified_expertise?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string
				}
			}[]
		}
	}[]
}

export const fields = [
	'anchor_title',
	'heading.title',
	'text.text',
	'review_text.text',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_download',
	'buttons.menu_items_id.href',
	'buttons.menu_items_id.label',

	'verified_expertise.id',
	'verified_expertise.site_content_collections_id.collection.item:site_content.title',
] as const

interface WhyChooseUsProps {
	data?: WhyChooseUsData
}

export default function WhyChooseUs({ data }: WhyChooseUsProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionIdBase = data?.anchor_title || 'WhyChooseUs'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	const verifiedExpertiseItems = (data?.verified_expertise || [])
		.flatMap(v => v?.site_content_collections_id?.collection || [])
		.map(c => c?.item?.title)
		.filter(Boolean) as string[]
	const hasVerifiedExpertise = verifiedExpertiseItems.length > 0
	const verifiedText1 = verifiedExpertiseItems[0] || ''
	const verifiedText2 = verifiedExpertiseItems[1] || ''
	const verifiedText3 = verifiedExpertiseItems[2] || ''

	return (
		<section
			id={sectionId}
			data-name={data?.anchor_title || 'WhyChooseUs'}
			ref={ref}
			className='WhyChooseUs relative pb-16 md:py-16 overflow-hidden'
		>
			<div className='container relative '>
				<div className='grid lg:grid-cols-12 gap-8'>
					{/* Left Column - Image */}
					<div className='max-md:w-[120%] max-md:ml-[-10%] md:col-span-12 lg:col-span-4 relative lg:-ml-32 lg:-mt-28'>
						<FadeIn
							isVisible={isVisible}
							delay={0}
							direction='left'
							className='relative w-full lg:w-[calc(100%+8rem)] h-[30rem] lg:h-full lg:min-h-[500px] lg:-ml-32'
						>
							<Image
								src={getReadableImageUrl(
									data?.image?.image_file?.image?.id || '',
									data?.image?.image_file?.image?.filename_disk || ''
								)}
							alt={data?.image?.alt || ''}
							fill
							className='object-cover object-top'
							loading='lazy'
						/>
						</FadeIn>
					</div>

					{/* Right Column - Content */}
					<div className='md:col-span-12 lg:col-span-8'>
						{/* Header */}
						<FadeIn isVisible={isVisible} delay={100} direction='right'>
							<h2
								dangerouslySetInnerHTML={sanitizeHTML(
									data?.heading?.title || ''
								)}
							/>
						</FadeIn>
						<FadeIn isVisible={isVisible} delay={200} direction='right'>
							<div
								className='mb-8 grid gap-4'
								dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
							/>
						</FadeIn>
						{/* Review text */}
						{data?.review_text?.text && (
							<FadeIn isVisible={isVisible} delay={300} direction='right'>
								<div
									className='mb-8 rounded-lg bg-[#625959] grid gap-4 p-10 text-white [&>p]:text-lg'
									dangerouslySetInnerHTML={sanitizeHTML(
										data?.review_text?.text || ''
									)}
								/>
							</FadeIn>
						)}
						{/* Top Two Cards */}
						<div className='grid grid-cols-1 xl:grid-cols-2 gap-8'>
							{(data?.content_collection?.slice(0, 2) || []).map(
								(card, index) => {
									const titleItem =
										card.site_content_collections_id.collection.find(
											c => c.item.title
										)
									const textItem =
										card.site_content_collections_id.collection.find(
											c => c.item.text
										)

									return (
										<FadeIn
											key={card.id}
											isVisible={isVisible}
											delay={300 + index * 400}
											direction='up'
										>
											<div className='bg-[#f1eae5] rounded-lg p-8 h-full'>
												<div className='flex md:gap-6 max-md:flex-wrap max-md:justify-center'>
													<div>
														<h3 className='text-3xl'>
															{titleItem?.item.title || ''}
														</h3>
														<div
															className='mt-5 grid gap-4'
															dangerouslySetInnerHTML={sanitizeHTML(
																textItem?.item.text || ''
															)}
														/>
													</div>
												</div>
											</div>
										</FadeIn>
									)
								}
							)}
						</div>
					</div>

					{/* Bottom Three Cards - Aligned with Image */}
					{(data?.content_collection?.slice(2) || []).map((card, index) => {
						const titleItem = card.site_content_collections_id.collection.find(
							c => c.item.title
						)
						const textItem = card.site_content_collections_id.collection.find(
							c => c.item.text
						)

						return (
							<FadeIn
								key={card.id}
								isVisible={isVisible}
								delay={500 + index * 500}
								direction='up'
								className='md:col-span-12 xl:col-span-4'
							>
								<div className='bg-[#f1eae5] rounded-lg p-8 h-full'>
									<div className='flex md:gap-6 max-md:flex-wrap max-md:justify-center'>
										<div>
											<h3 className='text-3xl'>
												{titleItem?.item.title || ''}
											</h3>
											<div
												className='mt-5 grid gap-4'
												dangerouslySetInnerHTML={sanitizeHTML(
													textItem?.item.text || ''
												)}
											/>
										</div>
									</div>
								</div>
							</FadeIn>
						)
					})}
				</div>

				{/* Verified Expertise Section */}
				<div className='mt-16 px-4 sm:px-8 md:px-20 py-2 flex flex-col justify-center items-center md:gap-9'>
					<FadeIn isVisible={isVisible} delay={900}>
						<div className='font-aileron text-3xl font-semibold uppercase leading-10 tracking-wide'>
							Verified Expertise
						</div>
					</FadeIn>

					{/* Expertise Row */}
					<div className='w-full max-w-7xl flex flex-col lg:flex-row justify-center items-center gap-0 lg:gap-[46px]'>
						{/* Item 1: Logo + Board Certified */}
						<FadeIn
							isVisible={isVisible}
							delay={1000}
							className='h-full flex flex-col lg:flex-row justify-center items-center gap-5 md:w-[450px] py-6 lg:py-0'
						>
							<div className='w-full md:w-[120px] md:h-[130px] flex justify-center items-center'>
								<Image
									src='/images/aacs-fellowship-director-logo.png'
									alt='AACS Fellowship Director'
									width={238}
									height={147}
									className='w-full md:w-auto max-h-[130px] object-contain'
								/>
							</div>
							{hasVerifiedExpertise && verifiedText1 ? (
								<div
									className='text-center md:text-left text-black text-2xl uppercase font-freightBig leading-8 tracking-widest [&_a]:underline'
									dangerouslySetInnerHTML={sanitizeHTML(verifiedText1)}
								/>
							) : (
								<div className='text-center md:text-left text-black text-2xl uppercase font-freightBig leading-8 tracking-widest'>
									<a href='https://ambrdfcs.org/' target='_blank'>
										Board-certified
									</a>
									<br />
									UK/EU/USA
								</div>
							)}
						</FadeIn>

						{/* Line Divider 1 */}
						<div className='w-full lg:w-[1px] h-[1px] lg:h-[132px] bg-black self-center' />

						{/* Item 2: Rhinoplasties */}
						<FadeIn
							isVisible={isVisible}
							delay={1100}
							className='h-full w-full lg:w-[283px] flex justify-center items-center py-6 lg:py-0'
						>
							{hasVerifiedExpertise && verifiedText2 ? (
								<div
									className='text-center md:text-left text-black text-2xl uppercase font-freightBig leading-8 tracking-widest'
									dangerouslySetInnerHTML={sanitizeHTML(verifiedText2)}
								/>
							) : (
								<div className='text-center md:text-left text-black text-2xl uppercase font-freightBig leading-8 tracking-widest'>
									&gt;1,000
									<br />
									rhinoplasties
								</div>
							)}
						</FadeIn>

						{/* Line Divider 2 */}
						<div className='w-full lg:w-[1px] h-[1px] lg:h-[132px] bg-black self-center' />

						{/* Item 3: Author */}
						<FadeIn
							isVisible={isVisible}
							delay={1200}
							className='h-full w-full lg:w-[440px] flex flex-col justify-center items-center py-6 lg:py-0'
						>
							{hasVerifiedExpertise && verifiedText3 ? (
								<div className='text-center md:text-left'>
									<span className='text-base uppercase leading-6 tracking-widest'>
										Author:
										<br />
									</span>
									<div
										className='text-2xl uppercase font-freightBig leading-8 tracking-widest [&_a]:underline'
										dangerouslySetInnerHTML={sanitizeHTML(verifiedText3)}
									/>
								</div>
							) : (
								<div className='text-center md:text-left'>
									<span className='text-base uppercase leading-6 tracking-widest'>
										Author:
										<br />
									</span>
									<span className='text-2xl uppercase font-freightBig tracking-widest'>
										The Complete Guide to Rhinoplasty
										<br />
									</span>
									<span className='text-2xl uppercase font-freightBig leading-8 tracking-widest'>
										<a href='https://www.cqc.org.uk/' target='_blank'>
											CQC-accredited clinic
										</a>
									</span>
								</div>
							)}
						</FadeIn>
					</div>

					{/* Expertise Buttons */}
					{data?.buttons && data.buttons.length > 0 && (
						<FadeIn
							isVisible={isVisible}
							delay={1300}
							className='w-full grid grid-cols-2 lg:flex lg:flex-wrap justify-center gap-4 mt-8 md:px-4'
						>
							{data.buttons.map((button, index) => {
								const isLast = index === data.buttons!.length - 1
								return (
									<Link
										key={button.menu_items_id.href}
										href={button.menu_items_id.href}
										className={`text-center ${
											isLast
												? 'btn col-span-2 lg:col-auto'
												: 'btn_white col-span-1 max-md:px-2'
										}`}
									>
										{button.menu_items_id.label}
									</Link>
								)
							})}
						</FadeIn>
					)}
				</div>
			</div>
		</section>
	)
}
